import type { App } from "@open-booking/server";
import type { CreateBlockedDatePayload, CreateIcalSourcePayload, UpdateIcalSourcePayload } from "@open-booking/shared";
import { createApi } from "@reduxjs/toolkit/query/react";
import { type EdenCall, edenBaseQuery } from "@/src/common/lib/eden-base-query";

type GetMyIcalSourcesResponse = App["~Routes"]["me"]["ical-sources"]["get"]["response"]["200"];
type GetMyBlockedDatesResponse = App["~Routes"]["me"]["blocked-dates"]["get"]["response"]["200"];
type GetPropertyIcalSourcesResponse = App["~Routes"]["properties"][":id"]["ical-sources"]["get"]["response"]["200"];
type AddIcalSourceResponse = App["~Routes"]["properties"][":id"]["ical-sources"]["post"]["response"]["200"];
type UpdateIcalSourceResponse = App["~Routes"]["ical-sources"][":id"]["patch"]["response"]["200"];
type DeleteIcalSourceResponse = App["~Routes"]["ical-sources"][":id"]["delete"]["response"]["200"];
type SyncIcalSourceResponse = App["~Routes"]["ical-sources"][":id"]["sync"]["post"]["response"]["200"];
type CreateBlockedDateResponse = App["~Routes"]["properties"][":id"]["blocked-dates"]["post"]["response"]["200"];
type DeleteBlockedDateResponse = App["~Routes"]["blocked-dates"][":id"]["delete"]["response"]["200"];

export const icalSyncApi = createApi({
	reducerPath: "icalSyncApi",
	baseQuery: edenBaseQuery(),
	tagTypes: ["IcalSource", "BlockedDate"],
	endpoints: (builder) => ({
		getMyIcalSources: builder.query<GetMyIcalSourcesResponse, void>({
			query: (): EdenCall<GetMyIcalSourcesResponse> => (client) => client.me["ical-sources"].get(),
			providesTags: ["IcalSource"],
		}),
		getMyBlockedDates: builder.query<GetMyBlockedDatesResponse, void>({
			query: (): EdenCall<GetMyBlockedDatesResponse> => (client) => client.me["blocked-dates"].get(),
			providesTags: ["BlockedDate"],
		}),
		getPropertyIcalSources: builder.query<GetPropertyIcalSourcesResponse, string>({
			query:
				(propertyId): EdenCall<GetPropertyIcalSourcesResponse> =>
				(client) =>
					client.properties({ id: propertyId })["ical-sources"].get(),
			providesTags: ["IcalSource"],
		}),
		addIcalSource: builder.mutation<AddIcalSourceResponse, { propertyId: string; data: CreateIcalSourcePayload }>({
			query:
				({ propertyId, data }): EdenCall<AddIcalSourceResponse> =>
				(client) =>
					client.properties({ id: propertyId })["ical-sources"].post(data),
			invalidatesTags: ["IcalSource"],
		}),
		updateIcalSource: builder.mutation<UpdateIcalSourceResponse, { id: string; data: UpdateIcalSourcePayload }>({
			query:
				({ id, data }): EdenCall<UpdateIcalSourceResponse> =>
				(client) =>
					client["ical-sources"]({ id }).patch(data),
			invalidatesTags: ["IcalSource", "BlockedDate"],
		}),
		deleteIcalSource: builder.mutation<DeleteIcalSourceResponse, string>({
			query:
				(id): EdenCall<DeleteIcalSourceResponse> =>
				(client) =>
					client["ical-sources"]({ id }).delete(),
			invalidatesTags: ["IcalSource", "BlockedDate"],
		}),
		syncIcalSource: builder.mutation<SyncIcalSourceResponse, string>({
			query:
				(id): EdenCall<SyncIcalSourceResponse> =>
				(client) =>
					client["ical-sources"]({ id }).sync.post(),
			invalidatesTags: ["IcalSource", "BlockedDate"],
		}),
		createBlockedDate: builder.mutation<CreateBlockedDateResponse, { propertyId: string; data: CreateBlockedDatePayload }>({
			query:
				({ propertyId, data }): EdenCall<CreateBlockedDateResponse> =>
				(client) =>
					client.properties({ id: propertyId })["blocked-dates"].post(data),
			invalidatesTags: ["BlockedDate"],
		}),
		deleteBlockedDate: builder.mutation<DeleteBlockedDateResponse, string>({
			query:
				(id): EdenCall<DeleteBlockedDateResponse> =>
				(client) =>
					client["blocked-dates"]({ id }).delete(),
			invalidatesTags: ["BlockedDate"],
		}),
	}),
});

export const {
	useGetMyIcalSourcesQuery,
	useGetMyBlockedDatesQuery,
	useGetPropertyIcalSourcesQuery,
	useAddIcalSourceMutation,
	useUpdateIcalSourceMutation,
	useDeleteIcalSourceMutation,
	useSyncIcalSourceMutation,
	useCreateBlockedDateMutation,
	useDeleteBlockedDateMutation,
} = icalSyncApi;
